import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, LogOut, Plus, Trash, Star } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminTestimonials = () => {
  const navigate = useNavigate();
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ name: "", location: "", rating: "5", text: "", date: new Date().toISOString().split('T')[0] });

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) navigate("/admin/login");
    else fetchTestimonials();
  }, [navigate]);

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/testimonials`);
      setTestimonials(response.data);
    } catch (error) {
      toast.error("Failed to load testimonials");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("adminToken");
    try {
      await axios.post(`${API}/admin/testimonials`, { ...formData, rating: parseInt(formData.rating) }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Testimonial added successfully!");
      setDialogOpen(false);
      setFormData({ name: "", location: "", rating: "5", text: "", date: new Date().toISOString().split('T')[0] });
      fetchTestimonials();
    } catch (error) {
      toast.error("Failed to add testimonial");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    const token = localStorage.getItem("adminToken");
    try {
      await axios.delete(`${API}/admin/testimonials/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Testimonial deleted!");
      fetchTestimonials();
    } catch (error) {
      toast.error("Failed to delete testimonial");
    }
  };

  return (
    <div className="min-h-screen bg-secondary/30">
      <div className="bg-primary text-primary-foreground py-6">
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
          <div className="flex items-center">
            <Link to="/admin"><ArrowLeft className="w-6 h-6 mr-6" /></Link>
            <span className="text-2xl font-heading font-bold">MURRAY</span>
            <span className="text-2xl font-heading font-light text-accent ml-2">REALTOR</span>
            <span className="ml-4 text-sm uppercase tracking-widest">Manage Testimonials</span>
          </div>
          <Button onClick={() => { localStorage.removeItem("adminToken"); navigate("/admin/login"); }} variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-primary h-10 px-6 rounded-none">
            <LogOut className="w-4 h-4 mr-2" />Logout
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-heading font-bold text-primary">Testimonials</h1>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-6 rounded-none"><Plus className="w-4 h-4 mr-2" />Add Testimonial</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add New Testimonial</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div><Label>Name *</Label><Input required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} /></div>
                <div><Label>Location *</Label><Input required value={formData.location} onChange={(e) => setFormData({...formData, location: e.target.value})} /></div>
                <div><Label>Rating *</Label>
                  <select value={formData.rating} onChange={(e) => setFormData({...formData, rating: e.target.value})} className="w-full h-10 px-3 border border-border rounded">
                    {[5,4,3,2,1].map(r => <option key={r} value={r}>{r} Stars</option>)}
                  </select>
                </div>
                <div><Label>Testimonial Text *</Label><Textarea required rows={4} value={formData.text} onChange={(e) => setFormData({...formData, text: e.target.value})} /></div>
                <div><Label>Date *</Label><Input required type="date" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} /></div>
                <Button type="submit" className="w-full bg-primary">Add Testimonial</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? <div className="text-center py-20"><p>Loading...</p></div> : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {testimonials.map((t) => (
              <Card key={t.id}>
                <CardContent className="p-6">
                  <div className="flex mb-4">{[...Array(t.rating)].map((_, i) => <Star key={i} className="w-5 h-5 text-accent fill-accent" />)}</div>
                  <p className="text-muted-foreground mb-4 italic">"{t.text}"</p>
                  <div className="flex justify-between items-center">
                    <div><p className="font-semibold">{t.name}</p><p className="text-sm text-muted-foreground">{t.location} • {t.date}</p></div>
                    <Button onClick={() => handleDelete(t.id)} variant="outline" size="sm" className="text-red-600"><Trash className="w-4 h-4" /></Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminTestimonials;
